import React, { useCallback } from "react";
import { Box } from "@mui/material";
import { useI18n } from "../../languages/providers/I18nProvider";

type Props = {
  color?: string;
  onSwitch?: (lang: "en" | "ar") => void;
};

const LanguageSwitch: React.FC<Props> = ({ color = "inherit", onSwitch }) => {
  const { lang, setLang, t } = useI18n();

  const choose = useCallback(
    (next: "en" | "ar") => {
      if (next === lang) return;
      if (onSwitch) {
        onSwitch(next); // URL-based navigation (Header)
      } else {
        void setLang(next); // fallback: direct state switch (if used outside router)
      }
    },
    [lang, setLang, onSwitch],
  );

  const item = (active: boolean) =>
    ({
      appearance: "none",
      background: "transparent",
      border: "none",
      padding: "2px 4px",
      cursor: "pointer",
      color: "inherit",
      fontFamily: "'Poppins', sans-serif",
      fontSize: 14,
      lineHeight: 1,
      fontWeight: active ? 700 : 500,
      opacity: active ? 1 : 0.55,
      transition: "opacity .2s ease",
    }) as const;

  return (
    <Box
      role="group"
      aria-label="Language"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        color,
        userSelect: "none",
        letterSpacing: "0.04em",
      }}
    >
      <button
        type="button"
        onClick={() => choose("en")}
        aria-pressed={lang !== "ar"}
        style={item(lang !== "ar")}
      >
        {t("lang:english")}
      </button>
      <span aria-hidden style={{ opacity: 0.4 }}>
        |
      </span>
      <button
        type="button"
        onClick={() => choose("ar")}
        aria-pressed={lang === "ar"}
        style={item(lang === "ar")}
      >
        {t("lang:arabic")}
      </button>
    </Box>
  );
};

export default LanguageSwitch;
